import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import AppBarButtonLink from 'components/shared/buttons/AppBarButtonLink';
import legalListFields from 'common/constants/query-fields/legal';
import routes from 'common/routes';
import AppBarContext from 'context/AppBarContext';
import legalSlice from 'store/modules/legal';

const PER_PAGE = 9;

const clamp = (lines) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

/**
 * Hub admin de Términos y condiciones con la MISMA vista de cards que Noticias
 * (sin imagen: ícono + chips de rol/idioma). La vista del CLIENTE no cambia.
 */
function LegalHubPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['legal', 'routes', 'buttons', 'news']);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const legals = useSelector((state) => state.legal.data.legals);
  const paginatorInfo = useSelector((state) => state.legal.paginatorInfo);
  const status = useSelector((state) => state.legal.status);
  const refresh = useSelector((state) => state.legal.refresh);

  const pageTitle = t('legal.list', { ns: 'routes' });

  const [page, setPage] = useState(1);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    dispatch(
      legalSlice.actions.list({
        fields: legalListFields,
        filters: {},
        pagination: { first: PER_PAGE, page },
      })
    );
  }, [dispatch, page, refresh]);

  useEffect(() => {
    setAppBarTitle(pageTitle);
    setAppBarButton(
      <AppBarButtonLink label={t('add', { ns: 'buttons', defaultValue: 'Añadir' })} path={routes.createLegal.path} />
    );
  }, [pageTitle, setAppBarButton, setAppBarTitle, t]);

  const total = paginatorInfo?.total || 0;
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));
  const from = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const to = Math.min(page * PER_PAGE, total);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(i18n.language === 'es' ? 'es-CO' : 'en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const confirmDelete = () => {
    if (toDelete) dispatch(legalSlice.actions.forceDelete([toDelete.id]));
    setToDelete(null);
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />

      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography component="h2" sx={{ color: 'text.primary', fontWeight: 800, letterSpacing: '-0.01em', fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
            {pageTitle}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('showingArticles', {
              ns: 'news',
              from,
              to,
              total,
              defaultValue: 'Mostrando {{from}} a {{to}} de {{total}} artículos',
            })}
          </Typography>
        </Box>
      </Stack>

      <LoadingBlock
        emptyBlockProps={{ message: t('empty', { ns: 'legal' }) }}
        isValid={legals.length > 0}
        skeletonProps={{ height: 180, rows: 3 }}
        status={status}
        type="legal">
        <>
          <Grid container spacing={3}>
            {legals.map((item) => (
              <Grid key={item.id} xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                <Box
                  onClick={() => navigate(routes.viewLegal.path.replace(':id', item.id))}
                  role="button"
                  aria-label={item.title}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'hairline.main',
                    backgroundColor: 'surface.content',
                    p: 2.5,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1), background-color 200ms cubic-bezier(0.32,0.72,0,1)',
                    '&:hover': { transform: 'translateY(-2px)', backgroundColor: 'surface.hover' },
                  }}>
                  <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <Tooltip title={t('edit', { ns: 'buttons', defaultValue: 'Editar' })}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); navigate(routes.editLegal.path.replace(':id', item.id)); }}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'gold.main' } }}>
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('delete', { ns: 'buttons', defaultValue: 'Eliminar' })}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setToDelete(item); }}
                        sx={{ color: 'text.secondary', '&:hover': { color: '#ff8a8a' } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <GavelOutlinedIcon sx={{ color: 'gold.main', fontSize: 22 }} />
                  <Typography sx={{ color: 'text.primary', fontWeight: 800, lineHeight: 1.3, fontSize: '1.05rem', pr: 5, ...clamp(2) }}>
                    {item.title}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                    <Chip size="small" label={item.role} sx={{ backgroundColor: 'rgba(249,189,78,0.14)', color: 'gold.main', fontWeight: 800, textTransform: 'capitalize' }} />
                    <Chip size="small" label={item.language?.toUpperCase()} sx={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'text.secondary', fontWeight: 700 }} />
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <CalendarMonthOutlinedIcon sx={{ fontSize: 15, color: 'gold.main' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                      {formatDate(item.created_at)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 'auto', pt: 0.5 }}>
                    <Typography variant="body2" sx={{ color: 'gold.main', fontWeight: 800 }}>
                      {t('readMore', { ns: 'news', defaultValue: 'Leer más' })}
                    </Typography>
                    <ArrowForwardIcon sx={{ fontSize: 15, color: 'gold.main' }} />
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>

          {lastPage > 1 && (
            <Stack alignItems="center" sx={{ mt: 4 }}>
              <Pagination
                count={lastPage}
                page={page}
                onChange={(_, p) => setPage(p)}
                sx={{
                  '& .MuiPaginationItem-root': { color: 'text.secondary' },
                  '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: 'gold.main', color: '#111418', fontWeight: 800 },
                }}
              />
            </Stack>
          )}
        </>
      </LoadingBlock>

      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title={t('deleteLegalTitle', { ns: 'legal', defaultValue: 'Eliminar términos y condiciones' })}
        message={toDelete ? `"${toDelete.title}"` : ''}
      />
    </>
  );
}

export default LegalHubPage;
